import { useEffect, useState } from "preact/hooks";
import { playMoment } from "../tour-moments";
import { api, ApiError } from "../api";
import { useLive } from "../live";
import { playAcknowledgement, playPeonLine } from "../audio";
import { formatGold, formatAgo } from "../format";
import { canAfford, rewardInputErrors, teslaLineShown, type Reward } from "../../shared/rewards.ts";
import { LUXURY_TIERS, type LuxuryTier } from "../../shared/economy.ts";
import { describeGrant } from "../../shared/tesla.ts";
import type { ClaimsSummary, Redemption, RewardClaim, RewardsSummary } from "../../shared/types";
import { useEconomyUI, usePurseUI, useUpgradesUI, useWagerUI } from "../features";
import { href } from "../router";
import { Gold } from "../components/Gold";
import { WoodCard, useWoodDelivered } from "../components/WoodCard";
import { ErrorBox, ScreenTitle, Spinner } from "../components/Shell";
import { BusyLabel } from "../components/BusyLabel";
import { PurseSection } from "../components/Purse";
import { WagersCard } from "../components/Wagers";
import { rewardActionLabel } from "../a11y-labels.ts";

/** One "Recent" row: a settled claim or a plain redemption, merged and sorted by time. */
interface RecentEntry {
  key: string;
  title: string;
  cost: number;
  at: string;
  note: string | null;
}

function recentFromClaims(claims: RewardClaim[], peonName: string): RecentEntry[] {
  return claims
    .filter((c) => c.received_at)
    .map((c) => ({
      key: `claim-${c.id}`,
      title: c.title,
      cost: c.cost,
      at: c.received_at as string,
      // The gold chip beside the note is the reward's cost: what you paid when you bought it, what you
      // were paid when you delivered it. The note says which way the gold went.
      note: c.owed_to === peonName ? `you paid ${c.owed_by ?? "the house"}` : `${c.owed_to} paid you`,
    }));
}

/**
 * The plain redemptions, less the ones a claim already stands for: a redeem writes the ledger row
 * and its claim in one batch with the same reward and the same moment (server/db.ts), so a
 * received reward was listed twice, once as "you paid X" and once plain.
 */
export function recentFromRedemptions(redemptions: Redemption[], claims: readonly Pick<RewardClaim, "reward_id" | "created_at">[] = []): RecentEntry[] {
  const claimed = new Set(claims.map((c) => `${c.reward_id}|${c.created_at}`));
  return redemptions
    .filter((r) => !claimed.has(`${r.reward_id}|${r.created_at}`))
    .map((r) => ({ key: `redemption-${r.id}`, title: r.title, cost: r.cost, at: r.created_at, note: null }));
}

/** Reached from the Rewards button in the account sheet (tap your name). */
export function Rewards({ peonName }: { peonName: string }) {
  const economyUIEnabled = useEconomyUI();
  const upgradesUIEnabled = useUpgradesUI();
  const purseUIEnabled = usePurseUI();
  const wagerUIEnabled = useWagerUI();
  const [summary, setSummary] = useState<RewardsSummary | null>(null);
  const [claims, setClaims] = useState<ClaimsSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  /** Redeem's word, under the Redeem button that was pressed. */
  const [claimed, setClaimed] = useState<{ id: string; text: string; error?: boolean } | null>(null);
  const [received, setReceived] = useState<string | null>(null);
  const [offerTitle, setOfferTitle] = useState("");
  const [offerBlurb, setOfferBlurb] = useState("");
  const [offerCost, setOfferCost] = useState("");
  const [offerTeslaAccess, setOfferTeslaAccess] = useState(false);
  const [offerError, setOfferError] = useState<string | null>(null);
  const [offerBusy, setOfferBusy] = useState(false);
  /** "Offer a reward" opens the form in place; it folds again once the reward is offered. */
  const [offerOpen, setOfferOpen] = useState(false);
  const [teslaConnected, setTeslaConnected] = useState(false);
  const { delivered, clearDelivered, observe: observeWoodOrder } = useWoodDelivered(peonName);

  const reload = useLive(async (initial) => {
    const [s, c, t] = await Promise.allSettled([
      api.rewards.summary(peonName),
      economyUIEnabled ? api.rewards.claims(peonName) : Promise.resolve(null),
      economyUIEnabled ? api.tesla.status() : Promise.resolve(null),
    ]);
    if (s.status === "fulfilled") {
      observeWoodOrder(s.value.open_order, upgradesUIEnabled);
      setSummary(s.value);
      setError(null);
    } else if (initial) setError(s.reason instanceof Error ? s.reason.message : "Could not load");
    if (c.status === "fulfilled") setClaims(c.value);
    setTeslaConnected(t.status === "fulfilled" && t.value ? t.value.connected : false);
  }, [peonName, economyUIEnabled, upgradesUIEnabled]);

  // The first time Rewards opens with the economy on, its cards (the "rewards-opened" moment,
  // src/tour-moments.ts), once the screen has drawn what they point at.
  const loaded = !!summary;
  useEffect(() => {
    if (loaded) playMoment("rewards-opened");
  }, [loaded, economyUIEnabled]);

  /** Scrolls the reward card's result line into view once it has rendered. */
  function keepInView(rewardId: string) {
    requestAnimationFrame(() =>
      document
        .querySelector(`[data-reward-id="${CSS.escape(rewardId)}"] .reward-result`)
        ?.scrollIntoView({ block: "nearest", behavior: "smooth" }),
    );
  }

  async function redeem(reward: Reward) {
    if (!confirm(`Spend ${formatGold(reward.cost)} on ${reward.title}?`)) return;
    setBusyId(reward.id);
    setError(null);
    setClaimed(null);
    try {
      const { balance, redemption, claim, tesla } = await api.rewards.redeem(peonName, reward.id);
      setSummary((s) => (s ? { ...s, balance, redemptions: [redemption, ...s.redemptions] } : s));
      if (economyUIEnabled) {
        setClaims((c) => (c ? { ...c, owed_to_me: [claim, ...c.owed_to_me], tesla: tesla ? [tesla, ...c.tesla] : c.tesla } : c));
      }
      void playAcknowledgement();
      void playPeonLine("redeem");
      setClaimed({ id: reward.id, text: `Claimed ${reward.title}. Enjoy!` });
      // The claim lands in "Owed to you" above, pushing this card down: keep its word in sight.
      keepInView(reward.id);
    } catch (e) {
      const text = e instanceof ApiError && e.status === 409 ? "Not enough gold" : e instanceof Error ? e.message : "Something went wrong";
      setClaimed({ id: reward.id, text, error: true });
      keepInView(reward.id);
    } finally {
      setBusyId(null);
    }
  }

  async function retire(reward: Reward) {
    if (!confirm(`Take down ${reward.title}? No one can redeem it after.`)) return;
    setBusyId(reward.id);
    setError(null);
    try {
      await api.rewards.retire(reward.id, peonName);
      setSummary((s) => (s ? { ...s, rewards: s.rewards.filter((r) => r.id !== reward.id) } : s));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not retire");
    } finally {
      setBusyId(null);
    }
  }

  async function gotIt(claim: RewardClaim) {
    if (!confirm(`Got your ${claim.title} from ${claim.owed_by ?? "the house"}?`)) return;
    setBusyId(claim.id);
    setError(null);
    setReceived(null);
    try {
      const updated = await api.rewards.claimReceived(claim.id, peonName);
      setClaims((c) => (c ? { ...c, owed_to_me: c.owed_to_me.filter((cl) => cl.id !== claim.id), recent: [updated, ...c.recent] } : c));
      setReceived("Marked received");
      void playPeonLine("got");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not mark received");
    } finally {
      setBusyId(null);
    }
  }

  async function offerReward(e: Event) {
    e.preventDefault();
    const cost = Number(offerCost);
    const input = { title: offerTitle, blurb: offerBlurb, cost };
    const problem = rewardInputErrors(input);
    if (problem) {
      setOfferError(problem);
      return;
    }
    if (offerBusy) return;
    void playAcknowledgement();
    setOfferBusy(true);
    setOfferError(null);
    try {
      const reward = await api.rewards.create({
        title: offerTitle.trim(),
        blurb: offerBlurb.trim(),
        cost,
        created_by: peonName,
        ...(offerTeslaAccess ? { tesla_access: true } : {}),
      });
      setSummary((s) => (s ? { ...s, rewards: [reward, ...s.rewards] } : s));
      // The new card sits at the top of the Rewards list, well above this form: bring it into view.
      requestAnimationFrame(() =>
        document.querySelector(`[data-reward-id="${CSS.escape(reward.id)}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" }),
      );
      setOfferTitle("");
      setOfferBlurb("");
      setOfferCost("");
      setOfferTeslaAccess(false);
      setOfferOpen(false);
    } catch (e) {
      setOfferError(e instanceof Error ? e.message : "Could not offer it");
    } finally {
      setOfferBusy(false);
    }
  }

  const recent = summary
    ? [
        ...(claims ? recentFromClaims(claims.recent, peonName) : []),
        ...recentFromRedemptions(summary.redemptions, claims ? [...claims.recent, ...claims.owed_to_me] : []),
      ]
        .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
        .slice(0, 20)
    : [];

  return (
    <>
      <main class="content content-wide">
        <ScreenTitle title="Rewards" />
        {error && <ErrorBox message={error} onRetry={summary ? undefined : reload} />}
        {!summary && !error && <Spinner />}
        {summary && (
          <>
            <div class="rewards-balance">
              <span class="stat-label">Your gold, {peonName}</span>
              <Gold amount={summary.balance} />
            </div>
            {received && <p class="muted small rewards-claimed" role="status">{received}</p>}


            {economyUIEnabled && claims && claims.owed_to_me.length > 0 && (
              <>
                <h2 class="rewards-heading">Owed to you</h2>
                <ul class="rewards-list">
                  {claims.owed_to_me.map((c) => {
                    const tesla = claims.tesla.find((g) => g.claim_id === c.id);
                    return (
                      <li key={c.id} class="card reward-card">
                        <div class="reward-heading">
                          <span class="reward-title">{c.title}</span>
                          <Gold amount={c.cost} />
                        </div>
                        <p class="reward-blurb">
                          You paid {c.owed_by ?? "the house"} · {formatAgo(c.created_at)}
                        </p>
                        {tesla &&
                          (tesla.status === "failed" ? (
                            <div class="form-error">{describeGrant(tesla)}</div>
                          ) : (
                            <p class="muted small">{describeGrant(tesla)}</p>
                          ))}
                        {tesla && tesla.status === "invited" && tesla.share_link && (
                          <a class="btn btn-ghost btn-block" href={tesla.share_link} target="_blank" rel="noopener">
                            Open in the Tesla app
                          </a>
                        )}
                        {c.gift_link && (
                          <a class="btn btn-ghost btn-block" href={c.gift_link} target="_blank" rel="noopener" aria-label={rewardActionLabel("Open your gift card", c.title)}>
                            Open your gift card
                          </a>
                        )}
                        <button type="button" class="btn btn-primary btn-block" disabled={busyId !== null} onClick={() => void gotIt(c)} aria-label={rewardActionLabel("Got it", c.title)}>
                          <BusyLabel busy={busyId === c.id} label="Marking…">Got it</BusyLabel>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}

            {economyUIEnabled && claims && claims.i_owe.length > 0 && (
              <>
                <h2 class="rewards-heading">You owe</h2>
                <ul class="rewards-history">
                  {claims.i_owe.map((c) => (
                    // What is owed is the reward itself; the gold already came to this peon when it was bought.
                    <li key={c.id} class="rewards-history-row">
                      <span class="rewards-history-title">{c.title}</span>
                      <span class="muted small">to {c.owed_to}, who paid you</span>
                      <Gold amount={c.cost} />
                      <span class="muted small">{formatAgo(c.created_at)}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {(economyUIEnabled || upgradesUIEnabled) && <h2 class="rewards-heading">Rewards</h2>}
            {summary.rewards.length === 0 && <p class="muted small">No rewards offered yet.</p>}
            <ul class="rewards-list">
              {summary.rewards.map((r) => {
                const afford = canAfford(summary.balance, r);
                const busy = busyId === r.id;
                const mine = economyUIEnabled && r.created_by === peonName;
                const tesla = teslaLineShown(r);
                return (
                  <li key={r.id} class="card reward-card" data-reward-id={r.id}>
                    {/* Title and cost, then who offers it: one block, no paragraph gap. */}
                    <div class="reward-top">
                      <div class="reward-heading">
                        <span class="reward-title">{r.title}</span>
                        <Gold amount={r.cost} />
                      </div>
                      {economyUIEnabled && <p class="reward-from">from {r.created_by ?? "the house"}</p>}
                    </div>
                    {(r.blurb || tesla) && (
                      <p class="reward-blurb">
                        {r.blurb}
                        {tesla && <span class="reward-tesla">Includes a day with the Tesla</span>}
                      </p>
                    )}
                    {mine ? (
                      <button type="button" class="btn btn-ghost btn-block" disabled={busyId !== null} onClick={() => void retire(r)}>
                        <BusyLabel busy={busy} label="Taking down…">Take down</BusyLabel>
                      </button>
                    ) : (
                      <button type="button" class="btn btn-primary btn-block" disabled={!afford || busyId !== null} onClick={() => void redeem(r)}>
                        <BusyLabel busy={busy} label="Claiming…">Redeem</BusyLabel>
                      </button>
                    )}
                    {claimed?.id === r.id &&
                      (claimed.error ? (
                        <div class="form-error reward-result" role="alert">{claimed.text}</div>
                      ) : (
                        <p class="muted small rewards-claimed reward-result" role="status">{claimed.text}</p>
                      ))}
                    {!mine && !afford && claimed?.id !== r.id && <p class="muted small reward-need">Need {formatGold(r.cost - summary.balance)} more</p>}
                  </li>
                );
              })}
            </ul>

            {wagerUIEnabled && <WagersCard peonName={peonName} balance={summary.balance} refreshKey={summary} />}

            {/* The spends follow the shop (refinement pass, 2026-10-05). The luxury card has its own
                heading, so it never reads as part of the Wood section (2026-09-25). */}
            {economyUIEnabled && (
              <>
                <h2 class="rewards-heading">Luxury task</h2>
                <ul class="rewards-list">
                  <li class="card reward-card luxury-offer">
                    <div class="reward-top">
                      <div class="reward-heading">
                        <span class="reward-title">
                          From <Gold amount={LUXURY_TIERS[0]} />
                        </span>
                      </div>
                      <p class="reward-from">Put up gold and the job sits up with the chores in a purple frame. Whoever finishes it gets half.</p>
                    </div>
                    {summary.balance >= LUXURY_TIERS[0] ? (
                      <a class="btn btn-primary btn-block" href={href({ name: "newTask", luxury: true })}>
                        Post
                      </a>
                    ) : (
                      <>
                        <button type="button" class="btn btn-primary btn-block" disabled>
                          Post
                        </button>
                        <p class="muted small reward-need">Need {formatGold(LUXURY_TIERS[0] - summary.balance)} more</p>
                      </>
                    )}
                  </li>
                </ul>
              </>
            )}

            {upgradesUIEnabled && (
              <>
                <h2 class="rewards-heading">Wood</h2>
                <WoodCard peonName={peonName} summary={summary} setSummary={setSummary} delivered={delivered} clearDelivered={clearDelivered} />
              </>
            )}

            {economyUIEnabled && (
              <>
                <h2 class="rewards-heading">Offer a reward</h2>
                {/* Folded until asked for, the way Wagers' "Challenge a peon" opens its names. */}
                <button
                  type="button"
                  class="btn-quiet offer-open"
                  aria-expanded={offerOpen}
                  aria-controls="offer-form"
                  onClick={() => {
                    setOfferOpen((o) => !o);
                    setOfferError(null);
                  }}
                >
                  Offer one
                </button>
                {offerOpen && (
                  <form id="offer-form" class="form offer-form" onSubmit={(e) => void offerReward(e)}>
                    <label class="field">
                      <span class="field-label">Title</span>
                      <input class="input" type="text" required maxLength={60} value={offerTitle} onInput={(e) => setOfferTitle(e.currentTarget.value)} />
                    </label>
                    <label class="field">
                      <span class="field-label">Details (optional)</span>
                      <textarea class="input" rows={2} maxLength={200} value={offerBlurb} onInput={(e) => setOfferBlurb(e.currentTarget.value)} />
                    </label>
                    <label class="field">
                      <span class="field-label">Cost in gold</span>
                      <input
                        class="input"
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={100000}
                        step={1}
                        required
                        value={offerCost}
                        onInput={(e) => setOfferCost(e.currentTarget.value)}
                      />
                    </label>
                    {teslaConnected && (
                      <label class={`field toggle big-job-toggle${offerTeslaAccess ? " on" : ""}`}>
                        <input type="checkbox" checked={offerTeslaAccess} onChange={(e) => setOfferTeslaAccess(e.currentTarget.checked)} />
                        <span>Grants a day with the Tesla</span>
                      </label>
                    )}
                    {offerError && <div class="form-error" role="alert">{offerError}</div>}
                    <button class="btn btn-primary btn-block" type="submit" disabled={offerBusy}>
                      <BusyLabel busy={offerBusy} label="Offering…">Offer it</BusyLabel>
                    </button>
                  </form>
                )}
              </>
            )}

            {/* Recent shows once there is something in it: no heading over an empty line. */}
            {economyUIEnabled && recent.length > 0 && (
              <>
                <h2 class="rewards-heading">Recent</h2>
                <ul class="rewards-history">
                  {recent.map((entry) => (
                    <li key={entry.key} class="rewards-history-row">
                      <span class="rewards-history-title">{entry.title}</span>
                      {entry.note && <span class="muted small">{entry.note}</span>}
                      <Gold amount={entry.cost} />
                      <span class="muted small">{formatAgo(entry.at)}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {!economyUIEnabled && summary.redemptions.length > 0 && (
              <>
                <h2 class="rewards-heading">Recent rewards</h2>
                <ul class="rewards-history">
                  {summary.redemptions.map((r) => (
                    <li key={r.id} class="rewards-history-row">
                      <span class="rewards-history-title">{r.title}</span>
                      <Gold amount={r.cost} />
                      <span class="muted small">{formatAgo(r.created_at)}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {purseUIEnabled && <PurseSection refreshKey={summary.balance} />}
          </>
        )}
      </main>
    </>
  );
}
